import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarAlt, faTag, faCalculator } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const goldColor = '#c5a059';
    const API_BASE = 'https://api.liber.am';

    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    // 1. Ֆունկցիա՝ HTML-ը և &nbsp;-ները մաքրելու համար (SEO Meta-ի համար)
    const stripHtmlForSEO = (html) => {
        if (!html) return "";
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return (doc.body.textContent || "").trim().replace(/\s+/g, ' ');
    };

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const res = await axios.get(`${API_BASE}/api/blogs/${slug}`);
                setBlog(res.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching blog post:", err);
                setLoading(false);
            }
        };
        fetchPost();
        window.scrollTo(0, 0);
    }, [slug]);

    // 2. Loading վիճակ (Google-ը սա չի ինդեքսավորի որպես բովանդակություն)
    if (loading) return (
        <div style={{ background: '#0a0a0b', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="spinner" />
        </div>
    );

    // 3. 404 վիճակ (Ավելացրինք noindex, որ սխալ էջը չմտնի Google)
    if (!blog) return (
        <div style={{ background: '#0a0a0b', minHeight: '100vh', textAlign: 'center', paddingTop: '200px', color: '#fff' }}>
            <Helmet>
                <title>Հոդվածը չի գտնվել | Liber Group</title>
                <meta name="robots" content="noindex, follow" />
            </Helmet>
            <h2>Հոդվածը չգտնվեց</h2>
            <button onClick={() => navigate('/blog')} style={{ color: goldColor, background: 'none', border: 'none', cursor: 'pointer', marginTop: '20px' }}>
                Վերադառնալ բլոգին
            </button>
        </div>
    );

    // Մաքրում ենք նկարագրությունը Meta թեգի համար
    const seoDescription = stripHtmlForSEO(blog.description || blog.content).substring(0, 160);
    const showCalculatorCTA = ['Օգտակար խորհուրդներ', 'Հատուկ առաջարկներ'].includes(blog.category);

    return (
        <div style={{ background: '#0a0a0b', minHeight: '100vh', paddingBottom: '100px' }}>
            {/* --- SEO ՄԱՍ (Այ սա է պատասխանատու տարբերվող ինդեքսավորման համար) --- */}
          <Helmet>
    <title>{`${blog.title} | Liber Group Blog`}</title>
    <meta name="description" content={seoDescription} />
    <link rel="canonical" href={`https://liber.am/blog/${blog.slug}`} />

    {/* Open Graph */}
    <meta property="og:title" content={blog.title} />
    <meta property="og:description" content={seoDescription} />
    <meta property="og:image" content={`${API_BASE}${blog.image}`} />
    <meta property="og:url" content={`https://liber.am/blog/${blog.slug}`} />
    <meta property="og:type" content="article" />
    <meta property="article:published_time" content={blog.date} />
    <meta property="article:section" content={blog.category} />

    {/* Structured Data - Ավելի լրացված տարբերակ */}
    <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `https://liber.am/blog/${blog.slug}`
            },
            "headline": blog.title,
            "description": seoDescription,
            "image": `${API_BASE}${blog.image}`,
            "author": {
                "@type": "Organization",
                "name": "Liber Group",
                "url": "https://liber.am"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Liber Group",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://liber.am/logo.png" // Քո լոգոյի իրական լինկը
                }
            },
            "datePublished": blog.date,
            "dateModified": blog.updated_at || blog.date // Եթե բազայում ունես թարմացման ամսաթիվ
        })}
    </script>
</Helmet>

            {/* --- ՎԻԶՈՒԱԼ ՄԱՍ --- */}
            <div style={{ position: 'relative', width: '100%', height: '50vh', minHeight: '400px', overflow: 'hidden' }}>
                <img 
                    src={`${API_BASE}${blog.image}`} 
                    alt={blog.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    loading="eager"
                />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(to bottom, transparent, #0a0a0b)' }} />
            </div>

            <div className="blog-container">
                <div className="blog-card" style={{ background: '#111113', padding: '40px', borderRadius: '25px', marginTop: '-100px', position: 'relative', zIndex: 10 }}>
                    <div className="meta-container" style={{ color: '#888', marginBottom: '20px', display: 'flex', gap: '20px', fontSize: '13px' }}>
                        <span><FontAwesomeIcon icon={faCalendarAlt} color={goldColor} /> {new Date(blog.date).toLocaleDateString('hy-AM')}</span>
                        <span><FontAwesomeIcon icon={faTag} color={goldColor} /> {blog.category}</span>
                    </div>

                    <h1 style={{ color: '#fff', fontSize: '32px', fontWeight: '900', marginBottom: '25px' }}>{blog.title}</h1>
                    
                    <div style={{ width: '60px', height: '4px', background: goldColor, marginBottom: '30px' }} />

                    {/* Բուն հոդվածի տեքստը */}
                    <div 
                        className="blog-content"
                        style={{ color: '#ccc', lineHeight: '1.8', fontSize: '17px' }}
                        dangerouslySetInnerHTML={{ __html: blog.content }} 
                    />

                    {showCalculatorCTA && (
                        <div className="cta-box" style={{ marginTop: '50px', padding: '30px', background: goldColor, borderRadius: '20px', textAlign: 'center', color: '#000' }}>
                            <FontAwesomeIcon icon={faCalculator} size="2x" />
                            <h3 style={{ margin: '15px 0' }}>ՀԵՏԱՔՐՔՐԵ՞Ց ԱՅՍ ԾԱՌԱՅՈՒԹՅՈՒՆԸ</h3>
                            <Link to="/calculators">
                                <button style={{ background: '#000', color: '#fff', padding: '12px 25px', border: 'none', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer' }}>
                                    ՀԱՇՎԵԼ ԱՐԺԵՔԸ
                                </button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;